import React, { useState, useRef } from 'react';
import { useUploadBlogImageMutation } from 'store/services/blog/blogApi';
import './ImageUploadZone.css';

interface Props {
  onImageUploaded: (url: string) => void;
  currentImageUrl?: string;
  label: string;
  description?: string;
}

const ImageUploadZone: React.FC<Props> = ({
  onImageUploaded,
  currentImageUrl,
  label,
  description,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadImage] = useUploadBlogImageMutation();

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const validateFile = (file: File): string | null => {
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Invalid file type. Please upload a JPG, PNG, or WEBP image.';
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return 'File too large. Maximum size is 10MB.';
    }

    return null;
  };

  const handleFileUpload = async (file: File) => {
    console.log('🟢 handleFileUpload called', { name: file.name, size: file.size, type: file.type });
    setError(null);

    const validationError = validateFile(file);
    if (validationError) {
      console.log('🔴 Validation failed:', validationError);
      setError(validationError);
      return;
    }

    console.log('✅ Validation passed, starting upload');
    setUploadProgress(true);

    try {
      const formData = new FormData();
      formData.append('image', file);
      console.log('📤 About to call uploadImage API');

      const result = await uploadImage(formData).unwrap();
      onImageUploaded(result.data.url);
      setError(null);
    } catch (err: any) {
      console.error('🔴 Upload failed - Full error:', err);
      console.error('🔴 Upload failed - Error status:', err?.status);
      console.error('🔴 Upload failed - Error data:', err?.data);
      console.error('🔴 Upload failed - Error message:', err?.data?.message);
      console.error('🔴 Upload failed - Error errors:', err?.data?.errors);
      setError(err?.data?.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploadProgress(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    console.log('🟢 File dropped', e.dataTransfer.files);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      console.log('🟢 File dropped, calling handleFileUpload');
      handleFileUpload(files[0]);
    } else {
      console.log('🔴 No files in drop');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🟢 File input changed', e.target.files);
    const files = e.target.files;
    if (files && files.length > 0) {
      console.log('🟢 File selected, calling handleFileUpload');
      handleFileUpload(files[0]);
    } else {
      console.log('🔴 No files selected');
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onImageUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-upload-zone-container">
      <label className="image-upload-label">
        {label}
        {description && <span className="image-upload-description">{description}</span>}
      </label>

      <div
        className={`image-upload-zone ${isDragging ? 'dragging' : ''} ${currentImageUrl ? 'has-image' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        {uploadProgress ? (
          <div className="upload-progress">
            <div className="spinner"></div>
            <p>Uploading...</p>
          </div>
        ) : currentImageUrl ? (
          <div className="uploaded-image-preview">
            <img src={currentImageUrl} alt="Uploaded preview" />
            <div className="image-overlay">
              <button
                className="change-image-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                Change Image
              </button>
              <button className="remove-image-button" onClick={handleRemoveImage}>
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="upload-prompt">
            <svg
              className="upload-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="upload-text-primary">
              Drag & drop an image, or click to browse
            </p>
            <p className="upload-text-secondary">
              JPG, PNG, WEBP • Max 10MB
            </p>
          </div>
        )}
      </div>

      {error && <div className="upload-error">{error}</div>}
    </div>
  );
};

export default ImageUploadZone;
